export interface DealsColumn {
  id:
    | 'dealName'
    | 'stage'
    | 'client'
    | 'value'
    | 'dealsType'
    | 'currentStep'
    | 'closeDate'
    | 'owner'
    | 'actions';
  label: string;
  minWidth?: number;
  align?: 'right' | 'left' | 'center';
}

export const enum LayoutType {
  LIST = 'list',
  KANBAN = 'kanban',
}

export type DealType =
  | 'Commercial/Endorsement'
  | 'Event Appearance'
  | 'Event Owned'
  | 'Content/Production'
  | 'Licensing/IP'
  | 'Equity/Investment'
  | 'Branded Entertainment';

export const enum apiDealTypes {
  COMMERCIAL_ENDORSEMENT = 'COMMERCIAL_ENDORSEMENT',
  EVENT_APPEARANCE = 'EVENT_APPEARANCE',
  EVENT_OWNED = 'EVENT_OWNED',
  CONTENT_PRODUCTION = 'CONTENT_PRODUCTION',
  LICENSING_IP = 'LICENSING_IP',
  EQUITY_INVESTMENT = 'EQUITY_INVESTMENT',
  BRANDED_ENT = 'BRANDED_ENT',
}

export const PIPELINE_MAP: Record<apiDealTypes, DealType> = {
  COMMERCIAL_ENDORSEMENT: 'Commercial/Endorsement',
  EVENT_APPEARANCE: 'Event Appearance',
  EVENT_OWNED: 'Event Owned',
  CONTENT_PRODUCTION: 'Content/Production',
  LICENSING_IP: 'Licensing/IP',
  EQUITY_INVESTMENT: 'Equity/Investment',
  BRANDED_ENT: 'Branded Entertainment',
};

export const DealsTableColumns: readonly DealsColumn[] = [
  { id: 'dealName', label: 'Deal Name' },
  { id: 'stage', label: 'Stage' },
  { id: 'client', label: 'Client' },
  { id: 'value', label: 'Value' },
  { id: 'dealsType', label: 'Deals Type' },
  { id: 'currentStep', label: 'Current Step' },
  { id: 'closeDate', label: 'Close Date' },
  { id: 'owner', label: 'Owner' },
  { id: 'actions', label: '', align: 'right' },
];

interface BaseDealDetails {
  name: string;
  clientName: string;
  accountId?: string;
  ownerName: string;
  revenueTier: string;
  ceilingTier: string;
  clientOrientation: string;
  careerState: string;

  dealValue?: string;
  commissionRate: number;
  commissionValue: number;
  dealStructure: string;
  equity: number;
  backend: string;
  closeDate: Date;

  leadershipSponsor: string;
  source: string;
  partnerAccount: string;
  partnerContact: string;
  daysInCurrentStage: string;

  outcome: string;
  lossReason: string;
  passReason: string;
  competitor: string;

  notes: string;
}

export interface ICommercialEndorsement extends BaseDealDetails {
  stageName: string;
  clientFitScore: number;
  clientApproval: string;
  contractTerm: number;
  exclusivityScope: string;
  category: string;
  dealType: string;
  renewalProbability: string;
}

export interface IEventAppearance extends BaseDealDetails {
  stageName: string;
  eventName: string;
  eventType: string;
  eventDate: Date;
  eventLocation: string;
  duration: number;
  contentRights: string;
  paymentTerms: string;
  travelRequired: boolean;
}

export interface IEventOwned extends BaseDealDetails {
  stageName: string;
  eventPropertyName: string;
  ownershipStructure: string;
  '3ASOwnership': number;
  expectedAttendance: number;
  ticketRevenueTarget: number;
  sponsorshipRevenueTarget: number;
  productionBudget: number;
  projectedMargin: number;
}

export interface ILicensingIP extends BaseDealDetails {
  stageName: string;
  licenseType: string;
  AIRightsFlag: boolean;
  guaranteedMinimum: number;
  royaltyRate: number;
  territory: string;
  productApproval: string;
  reportingFrequency: string;
}

export interface IEquityInvestment extends BaseDealDetails {
  stageName: string;
  investmentType: string;
  entityName: string;
  industrySector: string;
  investmentAmount: number;
  ownershipTarget: number;
  preMoneyValuation: number;
  boardSeat: string;
  targetExitMultiple: number;
}

export interface IBrandedEntertainment extends BaseDealDetails {
  stageName: string;
  buyerType: string;
  organizationName: string;
  qualificationScore: number;
  clientPlacement: number;
  placedClients: string;
  engagementType: string;
  developmentFee: number;
  retainerValue: number;
  IPOwnership: number;
  lionsgateInvolved: boolean;
  recruitingPipeline: boolean;
}

export type DealsDetailsType = Partial<
  ICommercialEndorsement &
    IEventAppearance &
    IEventOwned &
    ILicensingIP &
    IEquityInvestment &
    IBrandedEntertainment &
    any
>;

type FieldType =
  | 'text'
  | 'number'
  | 'date'
  | 'select'
  | 'textarea'
  | 'section'
  | 'checkbox'
  | 'autocomplete';

export type FieldConfig = {
  name: string;
  label: string;
  type?: FieldType;
  options?: readonly string[];
  multiline?: boolean;
  minRows?: number;
  xs?: number;
  sm?: number;
  autocompleteType?: 'client' | 'owner' | 'company';
  required?: boolean;
};

export type Product = {
  PricebookEntryId: string;
  UnitPrice: number;
  id: string;
  name: string;
  Quantity?: string;
  ServiceDate?: string;
  description?: string;
};
